import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import CtaBlock from "@/components/seo/CtaBlock";
import RelatedGrid from "@/components/seo/RelatedGrid";
import useSeo from "@/hooks/useSeo";
import { getCaseStudyBySlug, getRelatedCases } from "@/data/seo/caseStudies";
import { getServiceBySlug } from "@/data/seo/services";
import { breadcrumbSchema, canonicalFor, SITE_BASE } from "@/lib/seoHelpers";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const cs = getCaseStudyBySlug(slug);

  const canonical = canonicalFor(`/work/${slug}`);
  const crumbs = cs
    ? [
        { label: "Home", href: "/" },
        { label: "Case Studies", href: "/work" },
        { label: cs.client, href: `/work/${slug}` },
      ]
    : [];

  useSeo({
    title: cs?.metaTitle,
    description: cs?.metaDescription,
    canonical,
    ogImage: cs?.cover,
  });

  if (!cs) return <Navigate to="/404" replace />;

  const schemas = [
    breadcrumbSchema(crumbs),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: cs.title,
      description: cs.metaDescription,
      image: [cs.cover],
      author: { "@type": "Organization", name: "InvitationWala", url: SITE_BASE },
      publisher: {
        "@type": "Organization",
        name: "InvitationWala",
        logo: { "@type": "ImageObject", url: `${SITE_BASE}/logos/invitation-wala.png` },
      },
      mainEntityOfPage: canonical,
    },
  ];

  const relatedCases = getRelatedCases(slug).map((c) => ({
    href: `/work/${c.slug}`,
    label: c.client,
    eyebrow: c.category,
    description: c.intro.slice(0, 120) + "…",
  }));

  const relatedServices = (cs.services || [])
    .map((s) => getServiceBySlug(s))
    .filter(Boolean)
    .map((s) => ({
      href: `/services/${s.slug}`,
      label: s.title,
      eyebrow: s.eyebrow,
      description: s.metaDescription.slice(0, 120) + "…",
    }));

  return (
    <SeoPageLayout schemas={schemas}>
      <article
        data-testid={`case-study-${slug}`}
        className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-8">
          <span className="overline text-[var(--iw-gold)]">{cs.category}</span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-5xl lg:text-6xl leading-[1.05] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {cs.title}
          </motion.h1>
          <div className="overline text-[var(--iw-faint)] mt-5">
            {cs.client} · {cs.role}
          </div>
        </div>

        <div
          className="relative aspect-[16/9] mb-10 overflow-hidden"
          style={{ border: "1px solid rgba(212,175,55,0.2)" }}
        >
          <img
            src={cs.cover}
            alt={`${cs.title} — InvitationWala case study`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        <blockquote
          className="border-l-2 pl-6 mb-10"
          style={{ borderColor: "var(--iw-gold)" }}
          data-testid="case-study-quote"
        >
          <p
            className="font-serif-display italic text-[var(--iw-ivory)] text-xl md:text-2xl leading-snug"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            “{cs.quote}”
          </p>
        </blockquote>

        <p
          className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mb-12 font-serif-display italic"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {cs.intro}
        </p>

        <div className="space-y-12">
          {cs.sections.map((sec, i) => (
            <section key={i} data-testid={`case-section-${i}`}>
              <h2
                className="font-serif-display text-[var(--iw-ivory)] text-2xl md:text-3xl leading-tight mb-4"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {sec.h2}
              </h2>
              {sec.paragraphs?.map((p, pi) => (
                <p
                  key={pi}
                  className="text-[var(--iw-muted)] leading-relaxed text-base md:text-lg mb-4"
                >
                  {p}
                </p>
              ))}
              {sec.bullets && (
                <ul className="mt-4 space-y-2.5">
                  {sec.bullets.map((b, bi) => (
                    <li
                      key={bi}
                      className="flex items-start gap-3 text-[var(--iw-muted)] leading-relaxed"
                    >
                      <span className="text-[var(--iw-gold)] mt-1 shrink-0">✦</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <CtaBlock
          title="Want an invitation like this?"
          subtitle="Tell us your story on WhatsApp — we'll share a tailored concept and quote within the hour."
          prefilledMessage={`Hi InvitationWala, I saw your "${cs.client}" case study and I'd like something similar for my celebration.`}
          testid={`cta-case-${slug}`}
        />

        {relatedServices.length > 0 && (
          <RelatedGrid
            title="Services behind this work"
            items={relatedServices}
            testid="case-related-services"
          />
        )}

        {relatedCases.length > 0 && (
          <RelatedGrid
            title="More Case Studies"
            items={relatedCases}
            testid="case-related-cases"
          />
        )}
      </article>
    </SeoPageLayout>
  );
}
